import type { FighterDTO, MonsterDTO, SkillId } from '@shared/protocol/messages';
import { RULES, isPersonalityId, isSkillId, skillDef, tierDef } from './rules';
import { t } from '../../i18n';

export interface StatBlock {
  hp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
}

/** Budget points spent by a stat block. Mirrors backend/internal/combat/budget.go. */
export function statPower(s: StatBlock): number {
  const b = RULES.budget;
  return s.hp * b.costHp + s.atk * b.costAtk + s.def * b.costDef + s.luck * b.costLuck + skillDef(s.skill).cost;
}

export interface Validation {
  ok: boolean;
  errors: string[];
}

function inRange(errors: string[], label: string, v: number, min: number, max: number): void {
  if (!Number.isInteger(v)) errors.push(t('{label} must be a whole number', { label }));
  else if (v < min || v > max) errors.push(t('{label} must be between {min} and {max}', { label, min, max }));
}

export function validateStatRanges(s: StatBlock): string[] {
  const r = RULES.stats;
  const errors: string[] = [];
  inRange(errors, 'HP', s.hp, r.hp.min, r.hp.max);
  inRange(errors, 'ATK', s.atk, r.atk.min, r.atk.max);
  inRange(errors, 'DEF', s.def, r.def.min, r.def.max);
  inRange(errors, 'LUCK', s.luck, r.luck.min, r.luck.max);
  if (!isSkillId(s.skill)) errors.push(t('Unknown skill'));
  return errors;
}

export function rewardCap(power: number): { exp: number; coins: number } {
  return {
    exp: Math.floor(power * RULES.budget.rewardExpPerPower),
    coins: Math.floor(power * RULES.budget.rewardCoinsPerPower),
  };
}

/** Public mode enforces the tier budget and reward caps; classroom only checks ranges. */
export function validateMonster(m: MonsterDTO): Validation {
  const errors = validateStatRanges(m);
  const name = m.name.trim();
  if (name.length < 1 || name.length > 24) errors.push(t('Name must be 1-24 characters'));
  if (!isPersonalityId(m.personality)) errors.push(t('Unknown personality'));
  if (!Number.isInteger(m.reward.exp) || m.reward.exp < 0) errors.push(t('EXP reward must be 0 or more'));
  if (!Number.isInteger(m.reward.coins) || m.reward.coins < 0) errors.push(t('Coin reward must be 0 or more'));
  if (errors.length === 0 && m.mode === 'public') {
    const power = statPower(m);
    const budget = tierDef(m.tier).budget;
    if (power > budget) errors.push(t('Stat budget exceeded: {power}/{budget}', { power, budget }));
    const cap = rewardCap(power);
    if (m.reward.exp > cap.exp) errors.push(t('EXP reward too high (max {max})', { max: cap.exp }));
    if (m.reward.coins > cap.coins) errors.push(t('Coin reward too high (max {max})', { max: cap.coins }));
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Scale a loadout down so it fits a budget (used to enter public PvP fairly).
 * Keeps skill and the ratio between stats; never goes below range minimums.
 */
export function fitToBudget(s: StatBlock, budget: number): StatBlock {
  const r = RULES.stats;
  const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));
  let out: StatBlock = {
    hp: clamp(s.hp, r.hp.min, r.hp.max),
    atk: clamp(s.atk, r.atk.min, r.atk.max),
    def: clamp(s.def, r.def.min, r.def.max),
    luck: clamp(s.luck, r.luck.min, r.luck.max),
    skill: s.skill,
  };
  if (statPower(out) <= budget) return out;
  const fixed = skillDef(s.skill).cost;
  const flexible = statPower(out) - fixed;
  const ratio = Math.max(0, budget - fixed) / Math.max(1, flexible);
  out = {
    hp: clamp(Math.floor(out.hp * ratio), r.hp.min, r.hp.max),
    atk: clamp(Math.floor(out.atk * ratio), r.atk.min, r.atk.max),
    def: clamp(Math.floor(out.def * ratio), r.def.min, r.def.max),
    luck: clamp(Math.floor(out.luck * ratio), r.luck.min, r.luck.max),
    skill: s.skill,
  };
  // Floors at range minimums can still overshoot: shave HP, then the rest.
  const order: (keyof Omit<StatBlock, 'skill'>)[] = ['hp', 'luck', 'def', 'atk'];
  let guard = 1000;
  while (statPower(out) > budget && guard-- > 0) {
    const key = order.find((k) => out[k] > r[k].min);
    if (!key) {
      if (out.skill === 'none') break;
      out = { ...out, skill: 'none' };
      continue;
    }
    out = { ...out, [key]: out[key] - 1 };
  }
  return out;
}

export function validateFighter(f: FighterDTO, mode: 'public' | 'classroom', freeStats: boolean): Validation {
  const errors = validateStatRanges({ hp: f.maxHp, atk: f.atk, def: f.def, luck: f.luck, skill: f.skill });
  const name = f.name.trim();
  if (name.length < 1 || name.length > 24) errors.push(t('Name must be 1-24 characters'));
  if (errors.length === 0 && !(mode === 'classroom' && freeStats)) {
    const power = statPower({ hp: f.maxHp, atk: f.atk, def: f.def, luck: f.luck, skill: f.skill });
    if (power > RULES.budget.pvpPublicBudget) errors.push(t('Over the arena budget ({power}/{budget})', { power, budget: RULES.budget.pvpPublicBudget }));
  }
  return { ok: errors.length === 0, errors };
}
